import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as d3 from 'd3';
import { motion } from 'framer-motion';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { tokenomicsConfig } from '@/data/tokenomicsData';
import { Lock, Unlock, Building, Coins, Vote as VoteIcon } from 'lucide-react';

export const AllocationExplorer = () => {
  const svgRef = useRef(null);
  const [filters, setFilters] = useState({
    unlockedAtTGE: true,
    lockedAtTGE: true,
    foundationControlled: true,
    marketFloat: true,
    daoControlled: true
  });
  const [dimensions, setDimensions] = useState({ width: 600, height: 350 });
  
  // Responsive dimensions
  useEffect(() => {
    const handleResize = () => {
      if (svgRef.current?.parentElement) {
        const parent = svgRef.current.parentElement;
        setDimensions({ 
          width: Math.min(parent.clientWidth - 40, 700), 
          height: 350 
        });
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Filter allocations based on toggle states
  const getFilteredAllocations = useCallback(() => {
    return tokenomicsConfig.allocations.filter(allocation => {
      if (allocation.unlockedAtTGE && !filters.unlockedAtTGE) return false;
      if (!allocation.unlockedAtTGE && !filters.lockedAtTGE) return false;
      if (allocation.foundationControlled && !filters.foundationControlled) return false;
      
      // Market float: liquidity, marketing, community
      const isMarketFloat = ['liquidity', 'community', 'marketing'].includes(allocation.id);
      if (isMarketFloat && !filters.marketFloat) return false;
      
      // DAO controlled: governance
      if (allocation.id === 'governance' && !filters.daoControlled) return false;
      
      return true;
    });
  }, [filters]);
  
  // D3 Bar Chart
  useEffect(() => {
    if (!svgRef.current) return;
    
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    
    const filteredData = getFilteredAllocations();
    const { width, height } = dimensions;
    const margin = { top: 20, right: 30, bottom: 80, left: 50 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;
    
    const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);
    
    // Scales
    const xScale = d3.scaleBand()
      .domain(filteredData.map(d => d.id))
      .range([0, innerWidth])
      .padding(0.3);
    
    const yScale = d3.scaleLinear()
      .domain([0, d3.max(filteredData, d => d.percentage) * 1.1])
      .range([innerHeight, 0]);
    
    // Grid lines
    g.append('g')
      .attr('class', 'grid')
      .call(d3.axisLeft(yScale)
        .tickSize(-innerWidth)
        .tickFormat('')
        .ticks(5)
      )
      .selectAll('line')
      .style('stroke', 'hsl(215, 20%, 20%)')
      .style('stroke-opacity', 0.3);
    
    g.selectAll('.grid .domain').remove();
    
    // Glow filter
    const defs = svg.append('defs');
    const filter = defs.append('filter')
      .attr('id', 'barGlow')
      .attr('x', '-50%')
      .attr('y', '-50%')
      .attr('width', '200%')
      .attr('height', '200%');
    
    filter.append('feGaussianBlur')
      .attr('stdDeviation', '3')
      .attr('result', 'coloredBlur');
    
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
    
    // Bars
    g.selectAll('.bar')
      .data(filteredData)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', d => xScale(d.id))
      .attr('y', innerHeight)
      .attr('width', xScale.bandwidth())
      .attr('height', 0)
      .attr('fill', d => d.color)
      .attr('rx', 4)
      .style('filter', 'url(#barGlow)')
      .style('cursor', 'pointer')
      .transition()
      .duration(800)
      .delay((d, i) => i * 100)
      .ease(d3.easeBackOut)
      .attr('y', d => yScale(d.percentage))
      .attr('height', d => innerHeight - yScale(d.percentage));
    
    // Percentage labels on bars
    g.selectAll('.bar-label')
      .data(filteredData)
      .enter()
      .append('text')
      .attr('class', 'bar-label')
      .attr('x', d => xScale(d.id) + xScale.bandwidth() / 2)
      .attr('y', d => yScale(d.percentage) - 8)
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('font-weight', '600')
      .style('fill', 'hsl(210, 20%, 98%)')
      .style('opacity', 0)
      .text(d => `${d.percentage}%`)
      .transition()
      .duration(800)
      .delay((d, i) => i * 100 + 400)
      .style('opacity', 1);
    
    // X Axis
    const xAxis = g.append('g')
      .attr('transform', `translate(0, ${innerHeight})`);
    
    xAxis.selectAll('.tick-label')
      .data(filteredData)
      .enter()
      .append('text')
      .attr('class', 'tick-label')
      .attr('x', d => xScale(d.id) + xScale.bandwidth() / 2)
      .attr('y', 20)
      .attr('text-anchor', 'middle')
      .style('font-size', '10px')
      .style('fill', 'hsl(210, 15%, 60%)')
      .each(function(d) {
        const text = d3.select(this);
        const words = d.name.split(' ');
        
        words.forEach((word, i) => {
          text.append('tspan')
            .attr('x', xScale(d.id) + xScale.bandwidth() / 2)
            .attr('dy', i === 0 ? 0 : 12)
            .text(word);
        });
      });
    
    // Lock/Unlock indicators
    xAxis.selectAll('.lock-indicator')
      .data(filteredData)
      .enter()
      .append('circle')
      .attr('cx', d => xScale(d.id) + xScale.bandwidth() / 2)
      .attr('cy', 55)
      .attr('r', 6)
      .attr('fill', d => d.unlockedAtTGE ? 'hsl(142, 60%, 45%)' : 'hsl(45, 85%, 55%)')
      .style('opacity', 0.8);
    
    // Y Axis
    g.append('g')
      .call(d3.axisLeft(yScale)
        .tickFormat(d => `${d}%`)
        .ticks(5)
      )
      .selectAll('text')
      .style('fill', 'hsl(210, 15%, 60%)')
      .style('font-size', '11px');
    
    g.selectAll('.domain').style('stroke', 'hsl(215, 20%, 25%)');
    g.selectAll('.tick line').style('stroke', 'hsl(215, 20%, 25%)');
    
  }, [filters, dimensions, getFilteredAllocations]);
  
  // Calculate totals based on filters
  const filteredTotal = getFilteredAllocations().reduce((sum, a) => sum + a.percentage, 0);
  
  return (
    <div className="space-y-6">
      {/* Filter Toggles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="glass rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Unlock className="w-4 h-4 text-success" />
              <Label htmlFor="unlocked" className="text-xs">Unlocked at TGE</Label>
            </div>
            <Switch
              id="unlocked"
              checked={filters.unlockedAtTGE}
              onCheckedChange={(checked) => setFilters(f => ({ ...f, unlockedAtTGE: checked }))}
            />
          </div>
        </div>
        
        <div className="glass rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-warning" />
              <Label htmlFor="locked" className="text-xs">Locked at TGE</Label>
            </div>
            <Switch
              id="locked"
              checked={filters.lockedAtTGE}
              onCheckedChange={(checked) => setFilters(f => ({ ...f, lockedAtTGE: checked }))}
            />
          </div>
        </div>
        
        <div className="glass rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building className="w-4 h-4 text-secondary" />
              <Label htmlFor="foundation" className="text-xs">Foundation</Label>
            </div>
            <Switch
              id="foundation"
              checked={filters.foundationControlled}
              onCheckedChange={(checked) => setFilters(f => ({ ...f, foundationControlled: checked }))}
            />
          </div>
        </div>
        
        <div className="glass rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Coins className="w-4 h-4 text-primary" />
              <Label htmlFor="float" className="text-xs">Market Float</Label>
            </div>
            <Switch
              id="float"
              checked={filters.marketFloat}
              onCheckedChange={(checked) => setFilters(f => ({ ...f, marketFloat: checked }))}
            />
          </div>
        </div>
        
        <div className="glass rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <VoteIcon className="w-4 h-4 text-accent" />
              <Label htmlFor="dao" className="text-xs">DAO Controlled</Label>
            </div>
            <Switch
              id="dao"
              checked={filters.daoControlled}
              onCheckedChange={(checked) => setFilters(f => ({ ...f, daoControlled: checked }))}
            />
          </div>
        </div>
      </div>
      
      {/* Filtered Total */}
      <motion.div 
        key={filteredTotal}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="glass rounded-xl p-4 text-center"
      >
        <p className="text-sm text-muted-foreground mb-1">Filtered Allocation Total</p>
        <p className="text-4xl font-bold text-primary">{filteredTotal}%</p>
      </motion.div>
      
      {/* Chart */}
      <div className="glass rounded-xl p-4 md:p-6">
        <svg ref={svgRef} className="w-full" />
        
        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-success" />
            <span className="text-muted-foreground">Unlocked at TGE</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-warning" />
            <span className="text-muted-foreground">Locked at TGE</span>
          </div>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">Unlocked at TGE</p>
          <p className="text-2xl font-bold text-success">80%</p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">Locked at TGE</p>
          <p className="text-2xl font-bold text-warning">20%</p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">Foundation Aligned</p>
          <p className="text-2xl font-bold text-secondary">35%</p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">Market Float</p>
          <p className="text-2xl font-bold text-primary">50%</p>
        </div>
      </div>
    </div>
  );
};

export default AllocationExplorer;
